/* DynamicNEETTest.jsx — Dynamic component for NEET tests with subject-wise structure */
import React, { useState, useEffect, useRef } from "react";
import "./pyqneettest.css";
import { auth, storage, db } from "../../../firebase";
import { ref as storageRef, listAll, getDownloadURL } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { doc, getDoc, setDoc, updateDoc, collection, addDoc } from "firebase/firestore";
import { getNEETQuestionType, getNEETSubject } from '../../../data/freeNEETTestData';

const SUBJECTS = ["Physics", "Chemistry", "Biology"];

export default function DynamicNEETTest({ testsMetadata }) {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { currentUser, loginWithGoogle } = useAuth();
  
  const testConfig = testsMetadata[testId];
  
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentSubject, setCurrentSubject] = useState(SUBJECTS[0]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({ Physics: {}, Chemistry: {}, Biology: {} });
  const [marked, setMarked] = useState({ Physics: [], Chemistry: [], Biology: [] });
  const [visited, setVisited] = useState({ Physics: [], Chemistry: [], Biology: [] });
  const [allQuestions, setAllQuestions] = useState({ Physics: [], Chemistry: [], Biology: {} });
  const [showInstructions, setShowInstructions] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [showFSPrompt, setShowFSPrompt] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [selectedResultQ, setSelectedResultQ] = useState(null); // { subject, index } for floating circles
  const [resultActiveSubject, setResultActiveSubject] = useState('Physics');
  const [resultQLoading, setResultQLoading] = useState(false);
  const preloadResultImgRef = useRef(null);
  // Check live browser cache: complete+naturalWidth means image is decoded in memory right now
  const isImageCached = (url) => {
    if (!url) return true;
    const img = new Image();
    img.src = url;
    return img.complete && img.naturalWidth > 0;
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userName = currentUser?.displayName || currentUser?.email;

  useEffect(() => {
    if (testConfig) {
      setTimeLeft(testConfig.duration);
    }
  }, [testConfig]);

  // Fetch questions from single folder, divide by subjects
  useEffect(() => {
    if (!testConfig) {
      setLoadingQuestions(false);
      return;
    }

    let cancelled = false;

    const getQuestionMetadata = (subject, subjectQId) => {
      const correctAnswer = testConfig.answerKey[subject]?.[subjectQId] 
        ? String(testConfig.answerKey[subject][subjectQId]).trim() 
        : null;
      
      // ✅ NEET always has single correct answers
      return {
        section: 1,
        questionType: "singleCorrect", // All NEET questions are MCQ
        correctAnswer: correctAnswer || "",
      };
    };

    async function fetchAllQuestions() {
      setLoadingQuestions(true);
      const fetchedQuestions = { Physics: [], Chemistry: [], Biology: [] };

      try {
        const folderRef = storageRef(storage, testConfig.storagePath + "/");
        const res = await listAll(folderRef);

        const allItemsPromises = res.items.map(async (itemRef) => {
          const name = itemRef.name;
          const match = name.match(/(\d+)/);
          const globalId = match ? parseInt(match[1], 10) : null;

          if (globalId === null || globalId > testConfig.totalQuestions) {
            return null;
          }

          // ✅ Use helper function to determine subject
          const subject = getNEETSubject(globalId, testConfig.totalQuestions);
          
          // Calculate subject-specific question ID
          let subjectQuestionId = 0;
          if (testConfig.totalQuestions === 200) {
            // 200-question pattern
            if (subject === "Physics") subjectQuestionId = globalId;
            else if (subject === "Chemistry") subjectQuestionId = globalId - 50;
            else if (subject === "Biology") subjectQuestionId = globalId - 100;
          } else if (testConfig.totalQuestions === 180) {
            // 180-question pattern
            if (subject === "Physics") subjectQuestionId = globalId;
            else if (subject === "Chemistry") subjectQuestionId = globalId - 45;
            else if (subject === "Biology") subjectQuestionId = globalId - 90;
          }

          const url = await getDownloadURL(itemRef).catch(err => null);
          const metadata = getQuestionMetadata(subject, subjectQuestionId);

          return {
            id: subjectQuestionId,
            globalId: globalId,
            img: url,
            subject: subject,
            ...metadata,
          };
        });

        const processedItems = (await Promise.all(allItemsPromises)).filter(Boolean);
        
        processedItems.forEach(item => {
          if (item && item.subject) {
            fetchedQuestions[item.subject].push(item);
          }
        });

        for (const subject of SUBJECTS) {
          fetchedQuestions[subject].sort((a, b) => a.id - b.id);
        }

        if (!cancelled) {
          setAllQuestions(fetchedQuestions);
        }
      } catch (err) {
        console.error("Error listing storage folder:", err);
        if (!cancelled) setAllQuestions({ Physics: [], Chemistry: [], Biology: [] });
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    }

    fetchAllQuestions();
    return () => { cancelled = true; };
  }, [testConfig]);

  // Preload all question images into browser cache so palette switching is instant
  useEffect(() => {
    const allImgs = Object.values(allQuestions).flat();
    if (allImgs.length === 0) return;
    allImgs.forEach((q) => {
      if (q.img) {
        const img = new Image();
        img.src = q.img;
      }
    });
  }, [allQuestions]);

  useEffect(() => {
    if (showTest && !showResult && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showTest, showResult, timeLeft]);

  const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // ✅ ADD: Image zoom handlers
  const handleImageClick = (imageSrc) => {
    console.log('🖼️ Image clicked, zooming:', imageSrc);
    setZoomedImage(imageSrc);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseZoom = () => {
    setZoomedImage(null);
    document.body.style.overflow = 'auto';
  };

  // Hide navbar when test is active
  useEffect(() => {
    if (showTest || showResult) {
      document.body.classList.add('test-fullscreen-mode');
    } else {
      document.body.classList.remove('test-fullscreen-mode');
    }
    return () => {
      document.body.classList.remove('test-fullscreen-mode');
    };
  }, [showTest, showResult]);

  const handleStartTestClick = () => {
    if (currentUser) {
      setShowInstructions(true);
    } else {
      setShowLoginPopup(true);
    }
  };
  
  const handleLoginClick = () => {
    // Must call loginWithGoogle() synchronously as the first action —
    // any setState or await before it causes browsers to block the popup.
    const loginPromise = loginWithGoogle();
    setShowLoginPopup(false);
    loginPromise
      .then(async (result) => {
        const uid = result.user.uid;
        const userRef = doc(db, "users", uid);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          await setDoc(userRef, {
            email: result.user.email,
            name: result.user.displayName,
            testsTaken: []
          });
        }
      })
      .catch((error) => {
        console.error("Google login failed:", error);
      });
  };

  const fullscreenActive = useRef(false);

  const requestTestFullscreen = () => {
    fullscreenActive.current = true;
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (req) req.call(el).catch(() => {});
  };
  const exitTestFullscreen = () => {
    fullscreenActive.current = false;
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) exit.call(document).catch(() => {});
  };
  const handleCloseTest = () => {
    exitTestFullscreen();
    navigate(testConfig.backLink);
  };
  // Re-enter fullscreen if user presses Esc (unintentional exit)
  useEffect(() => {
    const onFSChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && fullscreenActive.current) {
        setShowFSPrompt(true);
      }
    };
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  }, []);

  const handleProceedToTest = () => {
    if (agreeChecked) {
      requestTestFullscreen();
      setShowInstructions(false);
      setShowTest(true);
      setCurrentSubject(SUBJECTS[0]);
      setCurrentQuestionIndex(0);
    }
  };

  const getCurrentQuestions = () => allQuestions[currentSubject] || [];
  const getCurrentQuestion = () => {
    const questions = getCurrentQuestions();
    return questions[currentQuestionIndex] || null;
  };

  const goToQuestion = (idx, subj) => {
    const targetSubject = subj !== undefined ? subj : currentSubject;
    const qs = allQuestions[targetSubject] || [];
    const q = qs[idx];
    // Show spinner only if image is NOT currently in browser memory
    if (q && q.img && !isImageCached(q.img)) {
      setQuestionLoading(true);
    }
    if (subj !== undefined && subj !== currentSubject) setCurrentSubject(subj);
    setCurrentQuestionIndex(idx);
  };

  const handleAnswerChange = (value) => {
    const q = getCurrentQuestion();
    if (!q) return;

    setAnswers((prev) => {
      const subjectAnswers = { ...prev[currentSubject] };
      subjectAnswers[q.id] = value;
      return { ...prev, [currentSubject]: subjectAnswers };
    });
  };

  const handleSaveNext = () => {
    const questions = getCurrentQuestions();
    if (currentQuestionIndex < questions.length - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const handleClear = () => {
    const q = getCurrentQuestion();
    if (!q) return;
    
    setAnswers((prev) => {
      const subjectAnswers = { ...prev[currentSubject] };
      delete subjectAnswers[q.id];
      return { ...prev, [currentSubject]: subjectAnswers };
    });
  };

  const handleMarkReview = () => {
    const q = getCurrentQuestion();
    if (!q) return;

    setMarked((prev) => {
      const subjectMarked = [...(prev[currentSubject] || [])];
      if (!subjectMarked.includes(q.id)) {
        subjectMarked.push(q.id);
      }
      return { ...prev, [currentSubject]: subjectMarked };
    });
    handleSaveNext();
  };

  const handleMarkReviewOnly = () => {
    const q = getCurrentQuestion();
    if (!q) return;
    // Clear the answer so state becomes orange (not evaluated)
    setAnswers((prev) => {
      const subjectAnswers = { ...(prev[currentSubject] || {}) };
      delete subjectAnswers[q.id];
      return { ...prev, [currentSubject]: subjectAnswers };
    });
    setMarked((prev) => {
      const subjectMarked = [...(prev[currentSubject] || [])];
      if (!subjectMarked.includes(q.id)) {
        subjectMarked.push(q.id);
      }
      return { ...prev, [currentSubject]: subjectMarked };
    });
    handleSaveNext();
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    const questions = getCurrentQuestions();
    if (currentQuestionIndex < questions.length - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const handleSubjectChange = (subject) => {
    goToQuestion(0, subject);
  };

  // Track visited questions per subject
  useEffect(() => {
    const qs = allQuestions[currentSubject];
    if (!qs || qs.length === 0) return;
    const q = qs[currentQuestionIndex];
    if (!q) return;
    setVisited(prev => {
      if ((prev[currentSubject] || []).includes(q.id)) return prev;
      return { ...prev, [currentSubject]: [...(prev[currentSubject] || []), q.id] };
    });
  }, [currentQuestionIndex, currentSubject, allQuestions]);

  // Clear spinner: image questions → onLoad/onError on <img>; non-image questions → RAF after paint
  useEffect(() => {
    if (!questionLoading) return;
    const qs = allQuestions[currentSubject] || [];
    const q = qs[currentQuestionIndex];
    if (q && !q.img) {
      const id = requestAnimationFrame(() => setQuestionLoading(false));
      return () => cancelAnimationFrame(id);
    }
  }, [currentQuestionIndex, currentSubject, questionLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPaletteButtonClass = (q, index) => {
    const isAnswered = answers[currentSubject]?.[q.id];
    const isMarked = (marked[currentSubject] || []).includes(q.id);
    const isVisited = (visited[currentSubject] || []).includes(q.id);
    const isActive = currentQuestionIndex === index;

    let status = "";
    if (isAnswered && isMarked) status = "answered-marked";
    else if (isAnswered) status = "answered";
    else if (isMarked) status = "marked";
    else if (!isVisited) status = "not-visited";
    
    if (isActive) status = (status ? status + " " : "") + "active";
    return `palette-btn ${status}`;
  };

  const selectResultQ = (val, imgUrl) => {
    if (val === null) { setSelectedResultQ(null); return; }
    if (preloadResultImgRef.current) {
      preloadResultImgRef.current.onload = null;
      preloadResultImgRef.current.onerror = null;
      preloadResultImgRef.current = null;
    }
    setSelectedResultQ(val);
    if (!imgUrl || isImageCached(imgUrl)) { setResultQLoading(false); return; }
    setResultQLoading(true);
    const img = new Image();
    preloadResultImgRef.current = img;
    img.onload = () => { preloadResultImgRef.current = null; setResultQLoading(false); };
    img.onerror = () => { preloadResultImgRef.current = null; setResultQLoading(false); };
    img.src = imgUrl;
  };

  const selectResultSubject = (s) => {
    setResultActiveSubject(s);
    const qDetails = finalResult?.allQuestionDetails?.[s] || [];
    selectResultQ({ subject: s, index: 0 }, qDetails[0]?.img);
  };

  const handleSubmit = () => {
    setShowSubmitConfirm(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowSubmitConfirm(false);
    setShowResult(true);
    setShowTest(false);
  
    let totalMarks = 0;
    const subjectScores = { Physics: 0, Chemistry: 0, Biology: 0 };
    const allQuestionDetails = { Physics: [], Chemistry: [], Biology: [] };
    const stats = {
      totalQuestions: testConfig.totalQuestions,
      totalAttempted: 0,
      totalCorrect: 0,
      totalIncorrect: 0,
    };

    const maxMarks = {
      Physics: testConfig.questionCounts.Physics * 4,
      Chemistry: testConfig.questionCounts.Chemistry * 4,
      Biology: testConfig.questionCounts.Biology * 4,
    };

    for (const subject of SUBJECTS) {
      const subjectQuestions = allQuestions[subject];
      const subjectAnswers = answers[subject];
      
      subjectQuestions.forEach(q => {
        const userAnswer = subjectAnswers?.[q.id];
        const correctAnswer = q.correctAnswer;
        let questionMarks = 0;
        let status = 'unattempted';
        
        const isAnswered = userAnswer !== undefined && userAnswer !== null && userAnswer !== '';

        if (isAnswered) {
          stats.totalAttempted++;
          if (String(userAnswer) === String(correctAnswer)) {
            questionMarks = 4;
            status = 'correct';
            stats.totalCorrect++;
          } else {
            questionMarks = -1;
            status = 'incorrect';
            stats.totalIncorrect++;
          }
        }

        totalMarks += questionMarks;
        subjectScores[subject] += questionMarks;
        
        allQuestionDetails[subject].push({
          questionId: q.id,
          userAnswer: userAnswer || null,
          correctAnswer: correctAnswer,
          status: status,
          marks: questionMarks,
          img: q.img,
        });
      });
    }
  
    const totalMaxMarks = maxMarks.Physics + maxMarks.Chemistry + maxMarks.Biology;

    // Enrich subjectScores with accuracy
    const enrichedSubjectScores = {};
    for (const subj of SUBJECTS) {
      const subjAttempted = (allQuestionDetails[subj].filter(q => q.status !== 'unattempted').length);
      const subjCorrect = allQuestionDetails[subj].filter(q => q.status === 'correct').length;
      enrichedSubjectScores[subj] = {
        score: subjectScores[subj],
        correct: subjCorrect,
        incorrect: allQuestionDetails[subj].filter(q => q.status === 'incorrect').length,
        unattempted: allQuestionDetails[subj].filter(q => q.status === 'unattempted').length,
        accuracy: subjAttempted > 0 ? ((subjCorrect / subjAttempted) * 100).toFixed(1) : '0.0',
        total: maxMarks[subj],
      };
    }

    setFinalResult({
      totalMarks: totalMarks,
      totalMaxMarks: totalMaxMarks,
      subjectScores: subjectScores,
      enrichedSubjectScores,
      stats: stats,
      allQuestionDetails: allQuestionDetails,
      maxMarks: maxMarks,
    });
  
    if (currentUser) {
      const userRef = doc(db, "users", currentUser.uid);

      // Determine attempt number for this specific test
      let newAttempt = 1;
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const existing = (userDoc.data().testsTaken || []).filter(t => t.testName === testConfig.testName);
          newAttempt = existing.length + 1;
        }
      } catch (_) {}

      const resultData = {
        testId: testConfig.id,
        testName: testConfig.testName,
        testType: 'neet',
        score: totalMarks,
        total: totalMaxMarks,
        attempted: stats.totalAttempted,
        correct: stats.totalCorrect,
        incorrect: stats.totalIncorrect,
        unattempted: stats.totalQuestions - stats.totalAttempted,
        accuracy: stats.totalAttempted > 0 ? parseFloat(((stats.totalCorrect / stats.totalAttempted) * 100).toFixed(1)) : 0,
        totalQuestions: stats.totalQuestions,
        date: new Date().toISOString(),
        attempt: newAttempt,
        storagePath: testConfig.storagePath,
        enrichedSubjectScores: enrichedSubjectScores,
        questionDetails: allQuestionDetails,
      };

      try {
        // Save to scalable subcollection — failure must NOT block the legacy update
        try {
          await addDoc(collection(db, "users", currentUser.uid, "testResults"), resultData);
        } catch (subErr) {
          console.error("Subcollection write failed:", subErr);
        }

        // Always keep legacy array for backward compatibility
        const userDoc2 = await getDoc(userRef);
        if (userDoc2.exists()) {
          const testsTaken = userDoc2.data().testsTaken || [];
          testsTaken.push(resultData);
          await updateDoc(userRef, { testsTaken: testsTaken });
        } else {
          await setDoc(userRef, {
            email: currentUser.email,
            name: currentUser.displayName,
            testsTaken: [resultData]
          });
        }
      } catch (err) {
        console.error("Error saving result to Firebase:", err);
      }
    }
  };

  // Test not found
  if (!testConfig) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>Test Not Found</h2>
          <p>The requested test does not exist.</p>
          <button className="back-btn" onClick={() => navigate("/test-series/pyq/neet")}>
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  // Loading questions
  if (loadingQuestions) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>Loading {testConfig.testName}...</h2>
          <p>Organizing questions into sections. Please wait.</p>
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  // Login popup
  if (showLoginPopup) {
    return (
      <div className="modal-backdrop" style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1000
      }}>
        <div className="login-modal" style={{
          backgroundColor: '#fff', padding: '30px', borderRadius: '8px',
          textAlign: 'center', maxWidth: '400px', width: '90%',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <h3 style={{ color: '#ef4444', marginBottom: '20px' }}>Login Required</h3>
          <p style={{ marginBottom: '30px' }}>
            Please log in to start the test and save your progress.
          </p>
          <div className="button-row">
            <button
              onClick={handleLoginClick}
              className="start-test-btn"
              style={{
                backgroundColor: '#4285F4', color: 'white', padding: '10px 20px',
                border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px'
              }}
            >
              🔑 Login with Google
            </button>
            <button
              onClick={() => setShowLoginPopup(false)}
              className="back-btn"
              style={{
                backgroundColor: '#6b7280', color: 'white', padding: '10px 20px',
                border: 'none', borderRadius: '4px', cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ADD THIS: Initial info page
  if (!showInstructions && !showTest && !showResult) {
    const totalQuestions = (allQuestions.Physics?.length || 0) + 
                           (allQuestions.Chemistry?.length || 0) + 
                           (allQuestions.Biology?.length || 0);
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>{testConfig.testName}</h2>
          <p><strong>Subjects:</strong> {SUBJECTS.join(", ")}</p>
          <p><strong>Duration:</strong> 3 Hours</p>
          <p><strong>Total Questions:</strong> {totalQuestions}</p>
          <p><strong>Marking:</strong> +4 for Correct, -1 for Incorrect</p>
          <div className="button-row">
            <button className="start-test-btn" onClick={handleStartTestClick}>Start Test</button>
            <button className="back-btn" onClick={() => navigate(testConfig.backLink)}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ADD THIS: Instructions page
  if (showInstructions) {
    return (
      <div className="instructions-container">
        <h2>Please read the instructions carefully</h2>
        <div className="instruction-box">
          <h3>General Instructions:</h3>
          <p>Total duration of {testConfig.testName} is 180 min.</p>
          <p>The clock will be set at the server. The countdown timer will display the remaining time.</p>
          <p>The Questions Palette shows the status of each question:</p>
          <ul>
            <li>🔵 You have not visited the question yet.</li>
            <li>⚪ You have not answered the question.</li>
            <li>🟢 You have answered the question.</li>
            <li>🟡 You have NOT answered the question, but have marked the question for review.</li>
            <li>🟣 The question(s) "Answered and Marked for Review" will be considered for evaluation.</li>
          </ul>
          <h4>Answering a Question:</h4>
          <ul>
            <li>Click one of the radio buttons to select your answer.</li>
            <li>Click Save & Next to save your answer and move to the next question.</li>
            <li>Click Clear Response to clear your answer for the current question.</li>
          </ul>
          <h4>Navigating Through Sections:</h4>
          <ul>
            <li>Click on the Subject tabs ({SUBJECTS.join(", ")}) to switch between subjects.</li>
          </ul>
          <p className="declaration">
            I have read and understood the instructions.
          </p>
        </div>
        <div className="agree-section">
          <label>
            <input
              type="checkbox"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
            />
            {" "}I agree to the above instructions
          </label>
          <button
            className="proceed-btn"
            disabled={!agreeChecked}
            onClick={handleProceedToTest}
          >
            Start Test ➡
          </button>
        </div>
      </div>
    );
  }

  // ✅ Test interface - UPDATED with zoom modal
  if (showTest) {
    const questions = getCurrentQuestions();
    const q = getCurrentQuestion();

    if (!q) {
      return (
        <div className="initial-page-container">
          <div className="test-info-box">
            <h2>Loading Question...</h2>
            <p>Please wait while we load the question.</p>
            <div className="loader"></div>
          </div>
        </div>
      );
    }

    const currentAnswer = answers[currentSubject]?.[q.id];

    return (
      <div className="mock-container">
        {showFSPrompt && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: '20px'
          }}>
            <p style={{ color: '#fff', fontSize: '1.2rem', textAlign: 'center', margin: '0 20px' }}>
              ⚠️ Fullscreen is required during the test.<br/>Please return to fullscreen to continue.
            </p>
            <button
              onClick={() => { requestTestFullscreen(); setShowFSPrompt(false); }}
              style={{
                padding: '14px 36px', fontSize: '1rem', fontWeight: 600,
                background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: 'pointer'
              }}
            >
              🔲 Return to Fullscreen
            </button>
          </div>
        )}
        {showSubmitConfirm && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#1e293b', borderRadius: '12px', padding: '32px 28px',
              maxWidth: '380px', width: '90%', textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}>
              <p style={{ color: '#f1f5f9', fontSize: '1.1rem', marginBottom: '8px', fontWeight: 600 }}>Submit Test?</p>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '24px' }}>Once submitted, you cannot change your answers.</p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => setShowSubmitConfirm(false)}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #475569', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', fontWeight: 500 }}
                >Cancel</button>
                <button onClick={handleConfirmedSubmit}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                >Yes, Submit</button>
              </div>
            </div>
          </div>
        )}
        {/* ✅ Image zoom modal */}
        {zoomedImage && (
          <div className="image-modal-overlay">
            <div className="image-modal-backdrop" onClick={handleCloseZoom} />
            <div className="image-modal-content">
              <button className="image-modal-close" onClick={handleCloseZoom} aria-label="Close">
                ✕
              </button>
              <img src={zoomedImage} alt="Question Full View" className="image-modal-img" />
            </div>
          </div>
        )}

        {/* Test header — sticky inside mock-container, matching JEE structure */}
        <div className="test-header-forced">
          <div className="header-left-info">
            <span className="user-emoji">👤</span>
            <div className="user-details">
              <div className="info-line">
                <span className="label-bold">Candidate Name:</span> 
                <span className="value-text">{userName || "Guest"}</span>
              </div>
              <div className="info-line">
                <span className="label-bold">Test Name:</span> 
                <span className="value-text">{testConfig?.testName || "NEET Test"}</span>
              </div>
              <div className="info-line">
                <span className="label-bold">Current Subject:</span> 
                <span className="value-text">{currentSubject}</span>
              </div>
            </div>
          </div>
          
          <div className="header-timer-section">
            <div className="timer-label">Remaining Time:</div>
            <div className="timer-big-value" style={{ color: timeLeft < 600 ? '#f44336' : '#2196F3' }}>
              {formatTime(timeLeft)}
            </div>
            <div className="timer-total">Total: {testConfig?.duration ? (testConfig.duration / 3600).toFixed(0) : '3'} Hours</div>
          </div>
        </div>

        {/* SUBJECT TABS — inside mock-container so sticky context is consistent with palette */}
        <div className="subject-tabs-bar">
          {SUBJECTS.map((subject) => (
            <button
              key={subject}
              className={`subject-tab-btn ${currentSubject === subject ? 'active' : ''}`}
              onClick={() => handleSubjectChange(subject)}
            >
              {subject}
            </button>
          ))}
        </div>

        <div className="mock-body">
          <div className="question-area">
            {questionLoading && (
              <div className="question-loading-overlay">
                <div className="question-spinner"></div>
                <p className="question-loading-text">Loading question...</p>
              </div>
            )}
            <div
              className="question-clickable"
              onClick={() => q.img && handleImageClick(q.img)}
              style={q.img ? { cursor: 'zoom-in' } : {}}
            >
              <h4>{currentSubject} - Question {q.id}</h4>
              {/* ✅ SINGLE image wrapper - removed duplicate */}
              {q.img && (
                <div className="image-section">
                  <div className="question-image-wrapper">
                    <img
                      src={q.img}
                      alt={`Question ${q.id}`}
                      onLoad={() => setQuestionLoading(false)}
                      onError={() => setQuestionLoading(false)}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Options */}
            {isDesktop ? (
              <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '600', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Options:</span>
                {[1, 2, 3, 4].map((n) => {
                  const val = String(n);
                  return (
                    <label key={n} className="option">
                      <input
                        type="radio"
                        name={`q${q.id}`}
                        value={val}
                        checked={currentAnswer === val}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                      />
                      {`${val})`}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div style={{ marginTop: '20px' }}>
                <span style={{ fontWeight: '600', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Options:</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                  {[1, 2, 3, 4].map((n) => {
                    const val = String(n);
                    return (
                      <label key={n} className="option">
                        <input
                          type="radio"
                          name={`q${q.id}`}
                          value={val}
                          checked={currentAnswer === val}
                          onChange={(e) => handleAnswerChange(e.target.value)}
                        />
                        {`${val})`}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="button-row">
              <button className="save-btn" onClick={handleSaveNext}>Save & Next</button>
              <button className="clear-btn" onClick={handleClear}>Clear Response</button>
              <button className="mark-only-btn" onClick={handleMarkReviewOnly}>Mark for Review</button>
              <button className="mark-btn" onClick={handleMarkReview}>Answered &amp; Mark for Review</button>
            </div>
            <div className="button-row" style={{ marginTop: "15px" }}>
              <button className="nav-btn" onClick={handleBack}>Back</button>
              <button className="nav-btn" onClick={handleNext}>Next</button>
              <button className="submit-btn" onClick={handleSubmit}>Submit Test</button>
              <button className="close-btn1" onClick={handleCloseTest}>❌ Close Test</button>
            </div>
          </div>
          
          {/* Question Palette */}
          <div className="palette-col">
          <div className="question-palette" style={{ display: 'flex', flexDirection: 'column' }}>
            <h4 style={{ margin: '0 0 15px 0', flexShrink: 0 }}>{currentSubject} Palette</h4>
            <div className="palette-grid" style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '15px', flex: 1, scrollbarWidth: 'thin' }}>
              {questions.map((qq, i) => {
                const qAnswer = answers[currentSubject]?.[qq.id];
                const isAnswered = qAnswer !== undefined && qAnswer !== null && qAnswer !== "";
                const isMarked = marked[currentSubject]?.includes(qq.id);
                const isVisited = (visited[currentSubject] || []).includes(qq.id);
                const isCurrent = currentQuestionIndex === i;

                let className = "palette-btn";
                if (isAnswered && isMarked) className += " answered-marked";
                else if (isAnswered) className += " answered";
                else if (isMarked) className += " marked";
                else if (!isVisited) className += " not-visited";
                if (isCurrent) className += " active";

                return (
                  <button
                    key={qq.id}
                    className={className}
                    onClick={() => goToQuestion(i)}
                  >
                    {qq.id}
                  </button>
                );
              })}
            </div>
            
            {/* Legend */}
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '8px',
              fontSize: '0.75rem',
              borderTop: '2px solid #eee',
              paddingTop: '12px',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#1976D2', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Not Visited</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: 'white', border: '2px solid #ccc', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Not Answered</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#2E7D32', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Answered</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#E65100', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Marked for Review (Not Eval.)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 'fit-content' }}>
                <span style={{ width: '16px', height: '16px', backgroundColor: '#6A1B9A', borderRadius: '3px', display: 'inline-block' }}></span>
                <span>Answered &amp; Marked (Eval.)</span>
              </div>
            </div>
          </div>
          </div>{/* end palette-col */}
        </div>
      </div>
    );
  }

  // Result page with subject-wise breakdown
  if (showResult && finalResult) {
    const { totalMarks, totalMaxMarks, enrichedSubjectScores, stats, allQuestionDetails } = finalResult;
    const accuracy = stats.totalAttempted > 0 ? ((stats.totalCorrect / stats.totalAttempted) * 100).toFixed(1) : '0.0';
    const unattempted = stats.totalQuestions - stats.totalAttempted;

    // Selected question detail for floating circles
    const selQData = selectedResultQ !== null
      ? (allQuestionDetails[selectedResultQ.subject] || [])[selectedResultQ.index]
      : null;

    return (
      <div className="mock-container">
        {showFSPrompt && (
          <div style={{ position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.92)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'20px' }}>
            <p style={{ color:'#fff',fontSize:'1.2rem',textAlign:'center',margin:'0 20px' }}>⚠️ Fullscreen required.</p>
            <button onClick={() => { requestTestFullscreen(); setShowFSPrompt(false); }} style={{ padding:'14px 36px',fontSize:'1rem',fontWeight:600,background:'#2563eb',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer' }}>🔲 Return to Fullscreen</button>
          </div>
        )}
        {zoomedImage && (
          <div className="image-modal-overlay">
            <div className="image-modal-backdrop" onClick={handleCloseZoom} />
            <div className="image-modal-content">
              <button className="image-modal-close" onClick={handleCloseZoom}>✕</button>
              <img src={zoomedImage} alt="Full View" className="image-modal-img" />
            </div>
          </div>
        )}

        <div className="result-header">
          <h2>📊 Test Result &amp; Analysis</h2>
          <button className="result-back-btn" onClick={handleCloseTest}>⬅️ Back to Test Series</button>
        </div>

        <div className="result-page-body">

          {/* ══════════════════════════════════════════════
              SECTION 1: RESULT SUMMARY
          ══════════════════════════════════════════════ */}
          <section className="result-section">
            <h3 className="result-section-title">📋 Result Summary</h3>
            <p className="result-test-name">{testConfig.testName}</p>
            <p className="result-candidate">👤 {userName}</p>

            <div className="result-top-cards">
              <div className="rtop-card score-card">
                <div className="rtop-label">SCORE</div>
                <div className="rtop-value">{totalMarks}<span className="rtop-max">/{totalMaxMarks}</span></div>
              </div>
              <div className="rtop-card accuracy-card">
                <div className="rtop-label">ACCURACY</div>
                <div className="rtop-value">{accuracy}<span className="rtop-max">%</span></div>
              </div>
              <div className="rtop-card attempted-card">
                <div className="rtop-label">ATTEMPTED</div>
                <div className="rtop-value">{stats.totalAttempted}<span className="rtop-max">/{stats.totalQuestions}</span></div>
              </div>
            </div>

            <div className="your-progress-box">
              <h4 className="progress-heading">Your Progress</h4>
              <div className="progress-row">
                <div className="prog-item correct-prog">
                  <span className="prog-icon">✅</span>
                  <span className="prog-label">Correct</span>
                  <span className="prog-count">{stats.totalCorrect}/{stats.totalQuestions}</span>
                  <span className="prog-marks">+{stats.totalCorrect * 4}</span>
                </div>
                <div className="prog-item incorrect-prog">
                  <span className="prog-icon">❌</span>
                  <span className="prog-label">Incorrect</span>
                  <span className="prog-count">{stats.totalIncorrect}/{stats.totalQuestions}</span>
                  <span className="prog-marks">-{stats.totalIncorrect}</span>
                </div>
                <div className="prog-item skipped-prog">
                  <span className="prog-icon">⏭</span>
                  <span className="prog-label">Skipped</span>
                  <span className="prog-count">{unattempted}/{stats.totalQuestions}</span>
                  <span className="prog-marks">0</span>
                </div>
              </div>
            </div>

            <div className="subject-perf-table-wrapper">
              <h4 className="subject-perf-heading">Section-wise Performance</h4>
              <div className="subject-perf-table">
                <div className="spt-header">
                  <span>Section</span><span>Score</span><span>Correct</span><span>Incorrect</span><span>Skipped</span><span>Accuracy</span>
                </div>
                {SUBJECTS.map(subj => {
                  const s = enrichedSubjectScores?.[subj] || {};
                  return (
                    <div key={subj} className="spt-row">
                      <span className={`spt-subject spt-${subj.toLowerCase().substring(0,4)}`}>{subj}</span>
                      <span>{s.score ?? 0}/{s.total ?? 0}</span>
                      <span className="spt-correct">{s.correct ?? 0}</span>
                      <span className="spt-incorrect">{s.incorrect ?? 0}</span>
                      <span className="spt-skipped">{s.unattempted ?? 0}</span>
                      <span>{s.accuracy ?? '0.0'}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════
              SECTION 2: QUESTION-WISE ANALYSIS
          ══════════════════════════════════════════════ */}
          <section className="result-section">
            <h3 className="result-section-title">🔍 Question-wise Analysis</h3>
            <p className="floating-q-hint">Tap a number to view details · 🟢 Correct &nbsp; 🔴 Incorrect &nbsp; ⚪ Skipped</p>

            {/* Subject tabs for result analysis */}
            <div className="result-subject-tabs">
              {SUBJECTS.map(s => (
                <button key={s} className={`result-subj-tab ${resultActiveSubject === s ? 'active' : ''}`} onClick={() => selectResultSubject(s)}>
                  {s}
                </button>
              ))}
            </div>

            <div className="floating-circles-row" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
              {(allQuestionDetails[resultActiveSubject] || []).map((qDetail, idx) => {
                const statusClass = qDetail.status === 'correct' ? 'fcircle-correct' : qDetail.status === 'incorrect' ? 'fcircle-wrong' : 'fcircle-skip';
                const isSelected = selectedResultQ?.subject === resultActiveSubject && selectedResultQ?.index === idx;
                return (
                  <button
                    key={`${resultActiveSubject}-${idx}`}
                    className={`floating-circle ${statusClass} ${isSelected ? 'fcircle-selected' : ''}`}
                    onClick={() => selectResultQ(isSelected ? null : { subject: resultActiveSubject, index: idx }, qDetail.img)}
                    title={`Q${qDetail.questionId}: ${qDetail.status}`}
                  >
                    {qDetail.questionId}
                  </button>
                );
              })}
            </div>

            {selQData && (
              <div key={`${selectedResultQ?.subject}-${selectedResultQ?.index}`} className={`result-q-detail-panel ${selQData.status === 'correct' ? 'panel-correct' : selQData.status === 'incorrect' ? 'panel-wrong' : 'panel-skip'}`}>
                <div className="rqdp-header">
                  <span className="rqdp-qnum">Q{selQData.questionId} — {selectedResultQ.subject}</span>
                  <button className="rqdp-close" onClick={() => setSelectedResultQ(null)}>✕</button>
                </div>
                {resultQLoading ? (
                  <div className="rqdp-spinner-wrap"><div className="rqdp-spinner"></div></div>
                ) : (
                  <>
                    {selQData.img && (
                      <div className="rqdp-img-wrapper" onClick={() => handleImageClick(selQData.img)} style={{ cursor: 'zoom-in' }}>
                        <img src={selQData.img} alt={`Q${selQData.questionId}`} className="rqdp-img" />
                        <span className="rqdp-zoom-hint">🔍 Tap to zoom</span>
                      </div>
                    )}
                    <div className="rqdp-answers">
                      <div className="rqdp-ans-row">
                        <span className="rqdp-ans-label">Your Answer:</span>
                        <span className={`rqdp-ans-val ${selQData.status === 'correct' ? 'ans-correct' : selQData.status === 'incorrect' ? 'ans-wrong' : 'ans-skip'}`}>
                          {selQData.userAnswer || 'Not Answered'}
                        </span>
                      </div>
                      <div className="rqdp-ans-row">
                        <span className="rqdp-ans-label">Correct Answer:</span>
                        <span className="rqdp-ans-val ans-correct">{selQData.correctAnswer || '—'}</span>
                      </div>
                      <div className="rqdp-ans-row">
                        <span className="rqdp-ans-label">Marks:</span>
                        <span className="rqdp-ans-val">{selQData.marks > 0 ? `+${selQData.marks}` : selQData.marks}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </section>

        </div>
      </div>
    );
  }

  return null;
}